import React, { useEffect, useState } from 'react';

import { LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import { fetchYowanPublicReviews } from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

const formatReviewerName = (name = '') => {
  const trimmedName = String(name).trim();

  if (!trimmedName) {
    return '';
  }

  return trimmedName.endsWith('様') ? trimmedName : `${trimmedName}様`;
};

const renderReviewerAttribution = (entry = {}) => {
  const reviewerName = formatReviewerName(entry.reviewerName);
  const reviewerTitle = entry.reviewerTitle || '';

  if (!reviewerName && !reviewerTitle) {
    return null;
  }

  return (
    <>
      {reviewerTitle ? <span>{reviewerTitle}</span> : null}
      {reviewerTitle && reviewerName ? ' ' : null}
      {reviewerName && entry.reviewerUrl ? (
        <a
          className="yowan-review-entry__author-link"
          href={entry.reviewerUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {reviewerName}
        </a>
      ) : reviewerName}
    </>
  );
};

const getReviewsHeaderImageUrl = (image = null) => {
  if (!image) {
    return '';
  }

  return image.preview_url || image.thumbnail_url || image.url || '';
};

const REVIEW_HEADER_PLACEHOLDER_PATTERNS = [
  /^Reviews page header image slot\.?$/i,
  /^Reviews公開画面のヘッダー右側に表示される画像です。?$/,
];

const normalizeReviewHeaderParagraphs = (paragraphs = []) =>
  paragraphs
    .map((paragraph) => String(paragraph || '').trim())
    .filter((paragraph) =>
      paragraph && !REVIEW_HEADER_PLACEHOLDER_PATTERNS.some((pattern) => pattern.test(paragraph)),
    );

const ReviewsPage = () => {
  const content = yowanPageContent['/reviews'];
  const metadata = getYowanRouteMeta('/reviews');
  const [entries, setEntries] = useState([]);
  const [headerSettings, setHeaderSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanPublicReviews()
      .then((payload) => {
        if (isMounted) {
          setHeaderSettings(payload.header);
          setEntries(payload.results);
        }
      })
      .catch(() => {
        if (isMounted) {
          setHeaderSettings(null);
          setEntries([]);
          setLoadError('ご感想を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const contributors = entries
    .filter((entry) => entry.reviewerName)
    .map((entry) => ({
      name: formatReviewerName(entry.reviewerName),
      anchor: `review-${entry.id || entry.displayOrder}`,
    }));
  const headerImage = headerSettings?.primary_image || null;
  const headerImageUrl = getReviewsHeaderImageUrl(headerImage);
  const normalizedHeaderParagraphs = normalizeReviewHeaderParagraphs(headerSettings?.paragraphs || []);
  const headerParagraphs = normalizedHeaderParagraphs.length
    ? normalizedHeaderParagraphs
    : content.header.paragraphs;

  return (
    <>
      <Section>
        <PageContainer>
          <div className="yowan-reviews-header">
            <div className="yowan-reviews-header__image" aria-hidden="true">
              {headerImageUrl ? (
                <img
                  src={headerImageUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '18rem',
                    objectFit: 'contain',
                    background: 'var(--surface-panel)',
                  }}
                />
              ) : isLoading ? (
                <LoadingSpinner className="yowan-public-image-loading" />
              ) : (
                <span className="yowan-public-image-fallback-label">{content.header.imageLabel}</span>
              )}
            </div>
            <div className="yowan-reviews-header__content c-surface-content">
              <div className="yowan-reviews-header__body">
                {headerParagraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
              <div className="yowan-reviews-header__notes">
                {content.header.notes.map((note) => (
                  <p key={note}>{note}</p>
                ))}
              </div>
              {contributors.length ? (
                <div className="yowan-reviews-header__contributors">
                  <p className="yowan-reviews-header__contributors-title">
                    {content.header.contributorsTitle}
                  </p>
                  <ul className="yowan-reviews-header__contributors-list">
                    {contributors.map((contributor) => (
                      <li key={contributor.anchor} className="yowan-reviews-header__contributors-item">
                        <a href={`#${contributor.anchor}`}>{contributor.name}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </PageContainer>
      </Section>
      <Section>
        <PageContainer>
          <div className="yowan-review-list">
            {entries.map((entry, index) => (
              <article
                key={entry.id || entry.displayOrder}
                id={`review-${entry.id || entry.displayOrder}`}
                className={`yowan-review-entry${
                  index % 2 === 0 ? ' yowan-review-entry--left' : ' yowan-review-entry--right'
                }`}
              >
                <h2 className="yowan-review-entry__title">{entry.title}</h2>
                {entry.reviewerName || entry.reviewerTitle ? (
                  <p className="yowan-review-entry__author">
                    {renderReviewerAttribution(entry)}
                  </p>
                ) : null}
                <div className="yowan-review-entry__body">
                  {entry.paragraphs.map((paragraph, paragraphIndex) => (
                    <p key={`${entry.id || entry.displayOrder}-paragraph-${paragraphIndex}`}>{paragraph}</p>
                  ))}
                </div>
              </article>
            ))}
            {!entries.length ? (
              <p className="yowan-admin-form__message">
                {isLoading ? <LoadingSpinner /> : loadError || '表示できるご感想はまだありません。'}
              </p>
            ) : null}
          </div>
        </PageContainer>
      </Section>
    </>
  );
};

export default ReviewsPage;
